import React, { useState, useEffect } from "react";
import {
  Activity,
  Zap,
  Droplet,
  Shield,
  Terminal,
  ArrowRight,
  Menu,
  X,
  Wind,
  Cpu,
  ShoppingCart,
  CheckCircle2,
  TrendingUp,
  AlertCircle,
  Download,
  Info,
  Play,
  FileText,
  Lock,
  Eye,
  LogOut // Added Logout Icon
} from "lucide-react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  CartesianGrid,
  Area,
  ComposedChart
} from "recharts";

/* ===================== 1. MQP DESIGN SYSTEM ===================== */
const THEME = {
  bg: "bg-slate-950",
  text: "text-slate-200",
  textMuted: "text-slate-500",
  card: "bg-slate-900/50 backdrop-blur-md border border-white/5",
  accent: "text-emerald-400",
  accentBg: "bg-emerald-500",
  accentBorder: "border-emerald-500/30",
  buttonGlow: "shadow-[0_0_15px_rgba(16,185,129,0.4)]",
  heroShadow: "shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)]",
};

// --- UI Primitives ---

const GlassCard = ({ children, className = "", onClick, highlight = false }) => (
  <div
    onClick={onClick}
    className={`${THEME.card} rounded-2xl p-6 transition-all duration-300 ease-out 
      ${onClick ? "cursor-pointer hover:scale-[1.01] hover:border-emerald-500/30" : ""} 
      ${highlight ? "border-emerald-500/20 bg-emerald-500/5" : ""}
      ${className}`}
  >
    {children}
  </div>
);

const Badge = ({ children, type = "neutral" }) => {
  const styles = {
    neutral: "bg-slate-800 text-slate-400 border-slate-700",
    verified: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    alert: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    active: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    essential: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold tracking-wider border ${styles[type]}`}>
      {children}
    </span>
  );
};

const SectionHeader = ({ title, promise, stakes, action }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
    <div className="max-w-2xl">
      <h2 className="text-3xl font-bold text-white tracking-tight mb-2">{title}</h2>
      <p className="text-emerald-400 font-medium mb-1">{promise}</p>
      <p className="text-slate-500 text-sm">{stakes}</p>
    </div>
    {action}
  </div>
);

const Button = ({ children, variant = "primary", onClick, icon: Icon, className = "" }) => {
  const base = "inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold transition-all duration-200 active:scale-[0.98]";
  const variants = {
    primary: `bg-emerald-400 text-slate-950 hover:bg-emerald-300 ${THEME.buttonGlow}`,
    secondary: "bg-slate-800 text-white border border-white/5 hover:bg-slate-700",
    ghost: "bg-transparent text-emerald-400 hover:bg-emerald-500/10 border border-emerald-500/30",
    outline: "bg-transparent text-slate-400 border border-slate-700 hover:text-white hover:border-slate-500",
    danger: "bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20"
  };
  return (
    <button onClick={onClick} className={`${base} ${variants[variant]} ${className}`}>
      {Icon && <Icon size={18} />}
      {children}
    </button>
  );
};

/* ===================== 2. MODALS ===================== */

const LensModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in duration-200">
      <div className="w-full max-w-4xl bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[90vh] overflow-y-auto">
        {/* Left: Manifesto */}
        <div className="p-8 md:w-1/2 bg-slate-950 border-r border-white/5">
          <div className="text-emerald-400 text-xs font-mono font-bold tracking-widest mb-4">OPERATOR & FATHER LENS</div>
          <h3 className="text-2xl font-bold text-white mb-6">We run companies and we run families. If we run down, both pay.</h3>
          
          <div className="space-y-6">
            <div>
              <h4 className="text-slate-400 text-sm font-bold mb-2">THE SLEEPLESS QUESTION</h4>
              <p className="text-slate-500 text-sm italic">"Will I still be strong enough when my kids hit their stride? How do I stop burnout from looking like work ethic?"</p>
            </div>
            <div>
              <h4 className="text-slate-400 text-sm font-bold mb-2">WHAT SUCCESS LOOKS LIKE</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500"/> A 15-minute protocol I actually execute.</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500"/> Telemetry that tells me the truth.</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500"/> A standard my kids can copy.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Right: Tactics */}
        <div className="p-8 md:w-1/2 bg-slate-900 flex flex-col justify-between">
          <div className="space-y-6">
             <div className="p-4 rounded-xl bg-slate-800/50 border border-white/5">
                <div className="text-xs text-slate-500 uppercase mb-1">Critical Question</div>
                <div className="text-white font-medium">"What’s the smallest set of blocks that move the needle?"</div>
             </div>
             <div className="p-4 rounded-xl bg-slate-800/50 border border-white/5">
                <div className="text-xs text-slate-500 uppercase mb-1">Critical Question</div>
                <div className="text-white font-medium">"Where’s the proof this works for men like me?"</div>
             </div>
          </div>
          
          <div className="mt-8 pt-6 border-t border-white/5">
             <p className="text-center text-slate-400 text-sm font-medium mb-4">Discipline beats intensity. Systems beat goals.</p>
             <Button variant="primary" className="w-full" onClick={onClose}>Return to Mission</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- NEW: Mobile Menu Overlay ---
const MobileMenu = ({ isOpen, onClose, activeTab, setActiveTab }) => {
  if (!isOpen) return null;
  
  const tabs = [
    { id: "mission", label: "Mission" },
    { id: "protocol", label: "Protocol" },
    { id: "telemetry", label: "Telemetry" },
    { id: "store", label: "Store" },
    { id: "privacy", label: "Privacy Policy" },
  ];

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950 flex flex-col p-6 animate-in slide-in-from-right duration-200">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-2 font-bold text-white text-xl">
           <div className="w-3 h-3 rounded-full bg-emerald-500" />
           MQP // MENU
        </div>
        <button onClick={onClose} className="p-2 bg-slate-900 rounded-full text-slate-400 hover:text-white">
          <X size={24} />
        </button>
      </div>
      
      <div className="flex flex-col gap-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              onClose();
            }}
            className={`text-left p-4 rounded-xl text-lg font-bold transition-colors ${
              activeTab === tab.id 
              ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" 
              : "text-slate-400 hover:text-white hover:bg-slate-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mt-auto border-t border-white/10 pt-6 text-center text-slate-500 text-sm">
        SYSTEM STATUS: <span className="text-emerald-500">OPTIMAL</span>
      </div>
    </div>
  );
};

/* ===================== 3. VIEWS (PAGES) ===================== */

// --- MISSION (HOME) ---
const MissionView = ({ onNavigate, onOpenLens }) => {
  const [signed, setSigned] = useState(false);

  return (
    <div className="space-y-16 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Hero */}
      <div className={`relative overflow-hidden rounded-3xl bg-slate-900 border border-white/5 p-8 md:p-16 text-center ${THEME.heroShadow}`}>
        {/* Vertical Bloom behind text */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1px] h-full bg-gradient-to-b from-transparent via-emerald-500/50 to-transparent blur-[40px]" />
        
        <div className="relative z-10 flex flex-col items-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-mono mb-8">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>MQP // SYSTEM ONLINE v2.1</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tighter mb-6">
            OPTIMIZE THE <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
              MACHINE
            </span>
          </h1>
          
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto leading-relaxed">
            A disciplined health system for men who lead—built to run in <span className="text-slate-200 font-semibold">15 minutes a day</span>.
          </p>

          <div className="text-sm text-red-400/80 bg-red-500/5 border border-red-500/10 px-4 py-2 rounded-lg mb-8">
            Warning: If you burn out, the team and the family pay the tax. We don't leave that to chance.
          </div>

          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <Button onClick={() => onNavigate('protocol')} icon={ArrowRight}>Initialize MQP</Button>
            <Button variant="secondary" onClick={() => onNavigate('telemetry')} icon={Activity}>View Telemetry</Button>
            <button onClick={onOpenLens} className="text-sm text-slate-500 hover:text-emerald-400 transition-colors underline underline-offset-4 decoration-slate-700">
              Why this matters
            </button>
          </div>
        </div>
      </div>

      {/* Validation */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <Shield className="text-emerald-400" size={20} />
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Clinical Validation</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { k: 'Speed of Aging', v: '0.69', sub: '-24% vs chronological', badge: 'VERIFIED' },
            { k: 'Liver Fat', v: '1.1%', sub: '-82% visceral reduction', badge: 'VERIFIED' },
            { k: 'Grey Matter', v: '+2.4%', sub: 'Volume increase via cognitive load', badge: 'VERIFIED' },
          ].map((card, i) => (
            <GlassCard key={i} className="relative group">
              <div className="flex justify-between items-start mb-4">
                <span className="text-slate-400 text-sm font-medium">{card.k}</span>
                <Badge type="verified">{card.badge}</Badge>
              </div>
              <div className="text-4xl font-mono font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">{card.v}</div>
              <div className="text-xs text-slate-500 border-t border-white/5 pt-3 mt-2">
                {card.sub}
              </div>
            </GlassCard>
          ))}
        </div>
      </div>

      {/* The Oath */}
      <div className="max-w-2xl mx-auto text-center border-t border-white/10 pt-16">
        <h3 className="text-lg font-bold text-white mb-6">The Operator's Oath</h3>
        <div className="text-slate-400 italic mb-8 space-y-2">
          <p>"I will not outsource my health."</p>
          <p>"I will run a small set of moves, every day."</p>
          <p>"I will lead by example, so my kids can copy the standard."</p>
        </div>
        <button 
          onClick={() => setSigned(!signed)}
          className={`px-6 py-2 rounded-full border transition-all ${signed ? 'bg-emerald-500/10 border-emerald-500 text-emerald-400' : 'border-slate-600 text-slate-400 hover:border-slate-400'}`}
        >
          {signed ? "✓ OATH SIGNED" : "SIGN THE OATH"}
        </button>
      </div>
    </div>
  );
};

// --- PROTOCOL (CONFIGURATOR) ---
const ProtocolView = () => {
  const [activeGoal, setActiveGoal] = useState("cognitive");
  
  const options = [
    { id: "cognitive", label: "Mental Clarity", icon: Zap, desc: "Focus, memory, processing." },
    { id: "physical", label: "Peak Output", icon: Activity, desc: "VO2 Max, strength, recovery." },
    { id: "aesthetic", label: "Skin & Hair", icon: Droplet, desc: "Collagen density, visual age." },
  ];

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <SectionHeader 
        title="Protocol Configuration" 
        promise="Generate a routine you'll actually run."
        stakes="Complex plans fail. Small blocks repeat."
        action={<Button variant="secondary" icon={Download}>Download Checklist</Button>}
      />

      {/* Configurator */}
      <div className="grid md:grid-cols-3 gap-6">
        {options.map((opt) => (
          <GlassCard 
            key={opt.id} 
            onClick={() => setActiveGoal(opt.id)}
            className={`border-2 relative ${activeGoal === opt.id ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-transparent'}`}
          >
             {activeGoal === opt.id && (
                <div className="absolute top-4 right-4 text-emerald-400"><CheckCircle2 size={20} /></div>
             )}
             <div className={`p-3 rounded-xl w-fit mb-4 transition-colors duration-200 ${activeGoal === opt.id ? 'bg-emerald-500 text-slate-950' : 'bg-slate-800 text-slate-400'}`}>
                <opt.icon size={24} />
             </div>
             <h3 className="text-xl font-bold text-white mb-2">{opt.label}</h3>
             <p className="text-sm text-slate-400">{opt.desc}</p>
          </GlassCard>
        ))}
      </div>

      {/* Generated Routine */}
      <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
        <div className="bg-slate-950 border-b border-white/5 px-4 py-2 flex items-center justify-between">
          <span className="text-xs font-mono text-emerald-400">MQP_ROUTINE_GENERATOR.EXE</span>
          <span className="text-xs font-mono text-slate-500">TARGET: {activeGoal.toUpperCase()}</span>
        </div>
        <div className="p-6 font-mono text-sm space-y-4">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { time: "MORNING", items: ["Green Giant Intake", "Zone 2 Cardio (45m)", "Creatine 5g", "SPF 30+"] },
              { time: "MIDDAY", items: ["Super Veggie Meal", "Olive Oil (1 tbsp)", "Deep Work Block"] },
              { time: "EVENING", items: ["Last Meal 11am", "Blue Light Blockers", "Magnesium", "Sleep 8.5h"] }
            ].map(block => (
              <div key={block.time}>
                <div className="text-xs font-bold text-slate-500 mb-3 tracking-wider">{block.time}</div>
                <ul className="space-y-2">
                  {block.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-300">
                      <span className="text-emerald-500">➜</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="primary" icon={CheckCircle2}>Apply Routine</Button>
      </div>
    </div>
  );
};

// --- TELEMETRY (DATA - CONNECTED TO WORKER) ---
const TelemetryView = () => {
  // CONFIG: USE PRODUCTION WORKER URL
  const API_URL = "https://agentdecadeprotocol.benton-161.workers.dev";

  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null); // Added Error State
  const [metrics, setMetrics] = useState({
    sleepScore: "--",
    hrv: "--",
    recovery: "--",
    graphData: [
      { day: 'M', val: 0 }, { day: 'T', val: 0 }, { day: 'W', val: 0 },
      { day: 'T', val: 0 }, { day: 'F', val: 0 }, { day: 'S', val: 0 }, { day: 'S', val: 0 },
    ]
  });

  // 1. On Mount: Check URL for token (OAuth return) OR check LocalStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("access_token");
    
    // If returning from OAuth with a token, save it
    if (urlToken) {
      localStorage.setItem("whoop_token", urlToken);
      // Clean URL to look nice
      window.history.replaceState({}, document.title, "/");
    }

    // If we have a token (either just saved or from before), sync data
    const token = urlToken || localStorage.getItem("whoop_token");
    if (token) {
      setConnected(true);
      handleSync(token); 
    }
  }, []);

  const handleLogin = () => {
    window.location.href = `${API_URL}/auth/whoop`;
  };

  const handleLogout = () => {
    localStorage.removeItem("whoop_token");
    setConnected(false);
    setErrorMsg(null);
    setMetrics({
        sleepScore: "--",
        hrv: "--",
        recovery: "--",
        graphData: []
    });
  };

  const handleSync = async (optionalToken) => {
    setLoading(true);
    setErrorMsg(null);
    
    // Use the passed token, or grab from storage
    const token = optionalToken || localStorage.getItem("whoop_token");

    if (!token) {
      setConnected(false);
      setLoading(false);
      return;
    }

    try {
      // Pass token in Query Param (to bypass cookie issues on different domains)
      const res = await fetch(`${API_URL}/api/sync?token=${token}`, {
        method: "GET"
      });
      
      if (res.status === 401) {
        setConnected(false);
        localStorage.removeItem("whoop_token");
        return;
      }

      const data = await res.json();
      
      if (data.error) {
        console.error("Worker Error:", data.error);
        setErrorMsg(`Sync Failed: ${data.details || data.error}`);
        return; 
      }

      setMetrics({
        sleepScore: Math.round(data.sleepScore),
        hrv: Math.round(data.hrv),
        recovery: data.recovery,
        graphData: data.graphData && data.graphData.length > 0 ? data.graphData : metrics.graphData
      });
      setConnected(true);
    } catch (e) {
      console.error("Sync failed", e);
      setErrorMsg("Connection Error: Could not reach MQP Server.");
    } finally {
      setLoading(false);
    }
  };

  // Custom Chart Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 border border-slate-700 p-3 rounded-lg shadow-xl">
          <p className="text-slate-400 text-xs mb-1 font-mono tracking-wider">{label}</p>
          <p className="text-emerald-400 font-mono font-bold text-lg">
            {payload[0].value} <span className="text-xs text-slate-500">ms</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <SectionHeader 
        title="Telemetry" 
        promise="Operational truth."
        stakes="If it's not in the numbers, it's not in the plan."
        action={
          connected ? (
            <div className="flex gap-2">
             <Button variant="ghost" onClick={() => handleSync()} icon={Activity} className={loading ? "opacity-50" : ""}>
               {loading ? "Syncing..." : "Refresh Data"}
             </Button>
             <Button variant="danger" onClick={handleLogout} icon={LogOut}>
               Disconnect
             </Button>
            </div>
          ) : (
             <Button variant="secondary" onClick={handleLogin} icon={Activity}>
               Connect WHOOP
             </Button>
          )
        }
      />
      
      {/* ERROR MESSAGE DISPLAY */}
      {errorMsg && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl flex items-center gap-3">
            <AlertCircle size={20} />
            <span className="font-mono text-sm">{errorMsg}</span>
        </div>
      )}

      {/* KPI Grid */}
      <div className="grid md:grid-cols-4 gap-4">
        {[
          { 
            label: "Recovery", 
            val: metrics.recovery, 
            unit: "%", 
            color: metrics.recovery === "--" ? "text-slate-500" : (metrics.recovery > 66 ? "text-emerald-400" : (metrics.recovery > 33 ? "text-amber-400" : "text-red-400")) 
          },
          { 
            label: "Sleep Perf", 
            val: metrics.sleepScore, 
            unit: "%", 
            color: metrics.sleepScore === "--" ? "text-slate-500" : "text-blue-400" 
          },
          { 
            label: "VO2 Max", 
            val: "--", // FIX: Changed "62" to "--" to reflect reality
            unit: "ml/kg", 
            color: "text-slate-500" // Greyed out since we don't have data
          }, 
          { 
            label: "HRV (Last Night)", 
            val: metrics.hrv, 
            unit: "ms", 
            color: metrics.hrv === "--" ? "text-slate-500" : "text-emerald-400" 
          }
        ].map((stat, i) => (
          <GlassCard key={i} className="flex flex-col items-center justify-center py-8">
            <div className={`text-4xl font-mono font-bold ${stat.color} mb-2`}>{stat.val}</div>
            <div className="text-xs text-slate-500 font-mono tracking-widest uppercase">{stat.label}</div>
          </GlassCard>
        ))}
      </div>

      {/* Trend & Insights */}
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <GlassCard>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-bold text-white flex items-center gap-2"><TrendingUp size={16}/> HRV Trend (7 Day)</h3>
              {connected && <Badge type="verified">LIVE DATA</Badge>}
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={metrics.graphData}>
                  <defs>
                    <linearGradient id="colorVal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#34d399" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#34d399" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                  <XAxis dataKey="day" stroke="#475569" fontSize={12} tickLine={false} axisLine={false} dy={10} />
                  <YAxis stroke="#475569" fontSize={12} tickLine={false} axisLine={false} domain={['dataMin - 5', 'dataMax + 5']} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="val" stroke="#34d399" strokeWidth={2} fillOpacity={1} fill="url(#colorVal)" />
                  <Line type="monotone" dataKey="val" stroke="#34d399" strokeWidth={2} dot={{r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#0f172a'}} activeDot={{ r: 6 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </GlassCard>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-white text-sm uppercase tracking-wider mb-2">Automated Insights</h3>
          {[
            { title: "Sleep Drift Detected", desc: "Lights dim at 9:30pm tonight.", type: "alert" },
            { title: "VO2 Plateau", desc: "Zone 2 × 35m @ 135 bpm advised.", type: "neutral" },
            { title: "Weekload Low", desc: "2× resistance micro-blocks added.", type: "neutral" }
          ].map((item, i) => (
            <GlassCard key={i} className="p-4" highlight={item.type === 'alert'}>
              <div className="flex gap-3">
                <div className={`mt-1 ${item.type === 'alert' ? 'text-amber-400' : 'text-slate-400'}`}>
                  {item.type === 'alert' ? <AlertCircle size={16} /> : <Info size={16} />}
                </div>
                <div>
                  <div className={`text-sm font-bold ${item.type === 'alert' ? 'text-amber-100' : 'text-slate-200'}`}>{item.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{item.desc}</div>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- STORE ---
const StoreView = () => (
  <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
    <SectionHeader 
      title="The MQP Stack" 
      promise="Buy exactly what the protocol requires."
      stakes="Inventory creates drag. We stock only what the routine uses."
      action={<Button variant="ghost">View COAs</Button>}
    />

    <div className="grid md:grid-cols-3 gap-6">
      {[
        { name: "MQP Green Giant", price: "$45", desc: "Chlorella, Spermidine, Creatine complex.", badge: "ESSENTIAL" },
        { name: "Super Veggie Mix", price: "$32", desc: "Black lentils, broccoli powder, sulforaphane.", badge: "ESSENTIAL" },
        { name: "Nutty Pudding", price: "$38", desc: "Macadamia, berries, pea protein isolate.", badge: "TREAT" },
      ].map((item, i) => (
        <GlassCard key={i} className="flex flex-col h-full">
          <div className="flex justify-between items-start mb-4">
            <Badge type={item.badge === 'ESSENTIAL' ? 'verified' : 'neutral'}>{item.badge}</Badge>
            <div className="text-xl font-bold text-white">{item.price}</div>
          </div>
          <div className="h-32 bg-slate-800/50 rounded-lg mb-4 flex items-center justify-center text-slate-600 border border-white/5">
            <span className="text-xs font-mono">PRODUCT_IMG_0{i+1}.PNG</span>
          </div>
          <h3 className="text-lg font-bold text-white mb-2">{item.name}</h3>
          <div className="flex gap-4 text-xs text-slate-500 mb-4 font-mono">
            <span>DOSE: 1 SCOOP</span>
            <span>WHEN: AM</span>
          </div>
          <p className="text-sm text-slate-400 mb-6 flex-grow">{item.desc}</p>
          <Button variant="primary" icon={ShoppingCart} className="w-full">Add to Cart</Button>
        </GlassCard>
      ))}
    </div>
  </div>
);

// --- NEW: PRIVACY POLICY VIEW ---
const PrivacyView = () => (
  <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-4xl mx-auto">
    <SectionHeader 
      title="Data Protocol & Privacy" 
      promise="Your biological data is your property."
      stakes="Trust is the currency of the system. We secure it."
    />

    <div className="space-y-8">
      <GlassCard className="prose prose-invert prose-emerald max-w-none">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
           <Shield className="text-emerald-400" size={20} /> 
           1. Data Collection & WHOOP Integration
        </h3>
        <p className="text-slate-400 text-sm leading-relaxed mb-6">
          MQP integrates with the WHOOP API to retrieve specific physiological metrics (Sleep, Recovery, HRV, VO2 Max). 
          We only request read-access to the scopes strictly necessary to power your Telemetry dashboard. 
          We do not write data to your WHOOP profile, nor do we access GPS or location data.
        </p>

        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
           <Lock className="text-emerald-400" size={20} /> 
           2. Storage & Security
        </h3>
        <p className="text-slate-400 text-sm leading-relaxed mb-6">
          Your OAuth tokens are stored securely using HTTP-Only cookies and server-side encryption. 
          Biological data is cached temporarily for performance and is never sold, rented, or shared with third parties.
          Your data exists solely to provide you with "Operational Truth" in your dashboard.
        </p>

        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
           <Eye className="text-emerald-400" size={20} /> 
           3. User Control
        </h3>
        <p className="text-slate-400 text-sm leading-relaxed">
          You may revoke MQP's access to your WHOOP data at any time via the WHOOP app settings or by logging out of the MQP System.
          Upon revocation, cached data is purged from our active session storage.
        </p>

        <div className="mt-8 pt-8 border-t border-white/10 text-xs text-slate-500 font-mono">
           LAST UPDATED: JAN 2026 // CONTACT: SECURITY@MQPSYSTEM.COM
        </div>
      </GlassCard>
    </div>
  </div>
);

/* ===================== 3. GLOBAL SHELL & TERMINAL ===================== */

const TerminalModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in zoom-in-95 duration-200">
      <div className="w-full max-w-2xl bg-slate-900 border border-emerald-500/30 rounded-lg shadow-2xl overflow-hidden">
        <div className="bg-slate-950 px-4 py-2 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2 text-emerald-400 text-xs font-mono font-bold">
            <Terminal size={14} /> MQP_ACCESS_TERMINAL
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X size={16}/></button>
        </div>
        <div className="p-4 h-64 bg-slate-900 font-mono text-sm">
          <div className="text-slate-500 mb-4">Type a command. Examples: 'log hrv 54', 'set goal strength', 'explain creatine'</div>
          <div className="flex items-center gap-2 text-white">
            <span className="text-emerald-500">➜</span>
            <input 
              autoFocus
              className="bg-transparent border-none outline-none w-full text-white placeholder-slate-700"
              placeholder="Waiting for input..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  const [activeTab, setActiveTab] = useState("mission");
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [lensOpen, setLensOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Keyboard shortcut for terminal (CMD+K)
  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setTerminalOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Handle URL Query Params for direct Privacy Link (Required for WHOOP OAuth)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab && ["mission", "protocol", "telemetry", "store", "privacy"].includes(tab)) {
      setActiveTab(tab);
    }
  }, []);

  const renderView = () => {
    switch (activeTab) {
      case "mission": return <MissionView onNavigate={setActiveTab} onOpenLens={() => setLensOpen(true)} />;
      case "protocol": return <ProtocolView />;
      case "telemetry": return <TelemetryView />;
      case "store": return <StoreView />;
      case "privacy": return <PrivacyView />;
      default: return <MissionView onNavigate={setActiveTab} />;
    }
  };

  return (
    <div className={`min-h-screen ${THEME.bg} ${THEME.text} font-sans selection:bg-emerald-500/30`}>
      
      {/* GLOBAL NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <div 
            onClick={() => setActiveTab('mission')}
            className="flex items-center gap-3 font-bold text-white tracking-tight text-xl cursor-pointer hover:opacity-80 transition-opacity"
          >
            <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-[0_0_10px_#10b981]" />
            MQP<span className="text-slate-600 font-mono text-sm mt-1 hidden sm:inline-block">//SYSTEM</span>
          </div>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-1 bg-slate-900/50 p-1 rounded-full border border-white/5">
            {[
              { id: "mission", label: "Mission" },
              { id: "protocol", label: "Protocol" },
              { id: "telemetry", label: "Telemetry" },
              { id: "store", label: "Store" },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                  activeTab === tab.id 
                  ? "bg-emerald-500 text-slate-950 shadow-sm" 
                  : "text-slate-400 hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Actions (Desktop) */}
          <div className="hidden md:flex items-center gap-4">
            <button 
              onClick={() => setTerminalOpen(true)}
              className="flex items-center gap-2 text-xs font-mono border border-emerald-500/30 text-emerald-400 px-3 py-1.5 rounded hover:bg-emerald-500/10 transition-colors"
            >
              <Terminal size={12} /> CMD+K
            </button>
          </div>
          
          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden text-slate-400 hover:text-white"
            onClick={() => setMenuOpen(true)}
          >
            <Menu size={24} />
          </button>
        </div>
      </nav>

      {/* MAIN CONTENT */}
      <main className="pt-24 pb-20 max-w-7xl mx-auto px-6">
        {renderView()}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-slate-600 text-sm font-mono flex items-center gap-2">
             <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
             SYSTEM STATUS: <span className="text-emerald-500">OPTIMAL</span>
          </div>
          
          <div className="flex gap-6 text-sm text-slate-500 font-medium items-center">
            <span>We don't sell hacks. We run systems.</span>
            <span className="hidden md:inline">•</span>
            <span>Evidence first.</span>
            <span className="hidden md:inline">•</span>
            <button 
              onClick={() => setActiveTab('privacy')} 
              className="hover:text-emerald-400 transition-colors cursor-pointer"
            >
              Privacy Policy
            </button>
          </div>

          <div className="text-slate-700 text-xs">
             © 2025 MQP. ALL RIGHTS RESERVED.
          </div>
        </div>
      </footer>

      {/* MODALS & OVERLAYS */}
      <MobileMenu 
        isOpen={menuOpen} 
        onClose={() => setMenuOpen(false)} 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
      />
      <TerminalModal isOpen={terminalOpen} onClose={() => setTerminalOpen(false)} />
      <LensModal isOpen={lensOpen} onClose={() => setLensOpen(false)} />
      
    </div>
  );
}
